import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Card, Typography, Button, Descriptions, Row, Col, Space, Spin, Tag, message, Tabs } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, WarningOutlined, InfoCircleOutlined, BellOutlined } from '@ant-design/icons';
import { getAlertById, updateAlert } from '@/api/alerts';
import { Alert } from '@/types/community/alert';
import { Comment as CustomComment } from '@/types/common';
import { useAuth } from '@/contexts/AuthContext';
import AlertModal from '@/components/features/community/AlertModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import CommentsFormSection from '@/components/common/CommentsFormSection';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteAlert } from '@/api/alerts';
import { AmenityTab, AssetTab, BlockTab, ApartmentUnitTab } from '@/components/tab';


const { Title, Text, Paragraph } = Typography;


const severityColors: Record<Alert['type'], string> = {
  emergency: 'red',
  maintenance: 'orange',
  security: 'purple',
  weather: 'blue',
  other: 'default',
};

const severityIcons: Record<Alert['type'], React.ReactNode> = {
  emergency: <WarningOutlined style={{ color: severityColors.emergency }} />,
  maintenance: <InfoCircleOutlined style={{ color: severityColors.maintenance }} />,
  security: <BellOutlined style={{ color: severityColors.security }} />,
  weather: <InfoCircleOutlined style={{ color: severityColors.weather }} />,
  other: <InfoCircleOutlined style={{ color: severityColors.other }} />,
};

const AlertDetailsPage: React.FC = () => {
  const { t } = useTranslation(['emergencyAlerts', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { alert: Alert } | null;
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();

  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<Error | undefined>(undefined);

  // Queries - Always fetch to ensure we have the latest data
  const { data: alert, isLoading, refetch } = useQuery({
    queryKey: ['alerts', id],
    queryFn: () => getAlertById(id!, firestoreUser!),
    enabled: !!id && !!firestoreUser,
    staleTime: 0, // Always consider data stale to allow immediate refetch
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Use state data if available and more recent, otherwise use fetched data
  const displayAlert = state?.alert && !alert ? state.alert : alert;

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (alertId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteAlert(alertId, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('emergencyAlerts:alertDeleted'));
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setIsDeleteModalVisible(false);
      setDeleteError(undefined);
      navigate('/community/emergency-alerts');
    },
    onError: (error: Error) => {
      setDeleteError(error);
    }
  });

  // Handlers
  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setIsDeleteModalVisible(true);
    setDeleteError(undefined);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['alerts', id] });
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteError(undefined);
  };

  const handleErrorDismiss = () => {
    setDeleteError(undefined);
  };

  // Comment handlers
  const handleAddComment = async (comment: CustomComment) => {
    if (!displayAlert || !firestoreUser) return;

    try {
      const newComment = {
        ...comment,
        createdAt: new Date()
      };

      const updatedComments = [...(displayAlert.comments || []), newComment];

      await updateAlert(displayAlert.id, { comments: updatedComments as any }, firestoreUser);
      
      message.success(t('common:createSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['alerts', id] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      await refetch();
    } catch (error: any) {
      console.error('Error adding comment:', error);
      message.error(error.message || t('common:createError'));
    }
  };

  const handleUpdateComment = async (commentIndex: number, _content: string) => {
    if (!displayAlert || !firestoreUser) return;

    try {
      const updatedComments = [...(displayAlert.comments || [])];
      updatedComments[commentIndex] = {
        ...updatedComments[commentIndex],

      };

      await updateAlert(displayAlert.id, { comments: updatedComments as any }, firestoreUser);
      
      message.success(t('common:updateSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['alerts', id] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      await refetch();
    } catch (error: any) {
      console.error('Error updating comment:', error);
      message.error(error.message || t('common:updateError'));
    }
  };

  const handleDeleteComment = async (commentIndex: number) => {
    if (!displayAlert || !firestoreUser) return;

    try {
      const updatedComments = [...(displayAlert.comments || [])];
      updatedComments.splice(commentIndex, 1);

      await updateAlert(displayAlert.id, { comments: updatedComments as any }, firestoreUser);
      
      message.success(t('common:deleteSuccess'));
      
      // Always invalidate and refetch to get the latest data
      queryClient.invalidateQueries({ queryKey: ['alerts', id] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      await refetch();
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      message.error(error.message || t('common:deleteError'));
    }
  };

  // Helper function to get reference information
  const getReferenceInfo = () => {
    const references = [];
    
    if (displayAlert?.block) {
      references.push({ 
        type: 'block', 
        id: displayAlert.block.id, 
        name: displayAlert.block.name,
        label: t('emergencyAlerts:blockDetails')
      });
    }
    
    if (displayAlert?.apartmentUnit) {
      references.push({ 
        type: 'apartmentUnit', 
        id: displayAlert.apartmentUnit.id, 
        name: displayAlert.apartmentUnit.name,
        label: t('emergencyAlerts:apartmentUnitDetails')
      });
    }
    
    if (displayAlert?.amenity) {
      references.push({ 
        type: 'amenity', 
        id: displayAlert.amenity.id, 
        name: displayAlert.amenity.name,
        label: t('emergencyAlerts:amenityDetails')
      });
    }
    
    if (displayAlert?.asset) {
      references.push({ 
        type: 'asset', 
        id: displayAlert.asset.id, 
        name: displayAlert.asset.name,
        label: t('emergencyAlerts:assetDetails')
      });
    }
    
    return references;
  };

  const referenceInfo = getReferenceInfo();

  // Loading state - only show if no state data is available
  if (isLoading && !state?.alert) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayAlert) {
    return <Card>Alert not found</Card>;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3}>{displayAlert.title}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Content with Tabs */}
      <Tabs 
        defaultActiveKey="details" 
        type="card"
        items={[
          {
            key: 'details',
            label: t('emergencyAlerts:alertDetails'),
            children: (
              <Card>
                <Descriptions bordered column={1}>
                  <Descriptions.Item label={t('emergencyAlerts:table.title')}>
                    <Text strong>{displayAlert.title}</Text>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('emergencyAlerts:table.message')}>
                    <Paragraph>{displayAlert.description}</Paragraph>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('emergencyAlerts:table.severity')}>
                    <Tag color={severityColors[displayAlert.type]} icon={severityIcons[displayAlert.type]}>
                      {t(`emergencyAlerts:severities.${displayAlert.type}` as const, displayAlert.type)}
                    </Tag>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label={t('emergencyAlerts:table.timestamp')}>
                    {new Date(displayAlert.startTime).toLocaleString()}
                  </Descriptions.Item>
                  
                  {displayAlert.endTime && (
                    <Descriptions.Item label={t('emergencyAlerts:table.endTime')}>
                      {new Date(displayAlert.endTime).toLocaleString()}
                    </Descriptions.Item>
                  )}
                  
                  <Descriptions.Item label={t('emergencyAlerts:table.status')}>
                    <Tag color={displayAlert.status === 'active' ? 'red' : displayAlert.status === 'resolved' ? 'green' : 'default'}>
                      {t(`emergencyAlerts:statuses.${displayAlert.status}` as const, displayAlert.status)}
                    </Tag>
                  </Descriptions.Item>
                  
                  {displayAlert.block && (
                    <Descriptions.Item label={t('emergencyAlerts:table.block')}>
                      {displayAlert.block.name}
                    </Descriptions.Item>
                  )}
                  
                  {displayAlert.apartmentUnit && (
                    <Descriptions.Item label={t('emergencyAlerts:table.apartmentUnit')}>
                      {displayAlert.apartmentUnit.name}
                    </Descriptions.Item>
                  )}
                  
                  {displayAlert.amenity && (
                    <Descriptions.Item label={t('emergencyAlerts:table.amenity')}>
                      {displayAlert.amenity.name}
                    </Descriptions.Item>
                  )}
                  
                  {displayAlert.asset && (
                    <Descriptions.Item label={t('emergencyAlerts:table.asset')}>
                      {displayAlert.asset.name}
                    </Descriptions.Item>
                  )}
                </Descriptions>
                
                {/* Comments Section */}
                <div style={{ marginTop: 24 }}>
                  <Typography.Title level={4}>{t('common:comments')}</Typography.Title>
                  <CommentsFormSection
                    comments={displayAlert.comments as any || []}
                    onAddComment={handleAddComment as any}
                    onUpdateComment={handleUpdateComment}
                    onDeleteComment={handleDeleteComment}
                    showForm={true}
                    maxHeight="400px"
                  />
                </div>
              </Card>
            )
          },
          ...referenceInfo.map((reference) => ({
            key: reference.type,
            label: reference.label,
            children: (
              <>
                {reference.type === 'amenity' && (
                  <AmenityTab amenityId={reference.id} />
                )}
                {reference.type === 'asset' && (
                  <AssetTab assetId={reference.id} />
                )}
                {reference.type === 'block' && (
                  <BlockTab blockId={reference.id} />
                )}
                {reference.type === 'apartmentUnit' && (
                  <ApartmentUnitTab apartmentUnitId={reference.id} />
                )}
              </>
            )
          }))
        ]}
      />

      {/* Edit Modal */}
      <AlertModal
        open={isEditModalOpen}
        alert={displayAlert}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('emergencyAlerts:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
};

export default AlertDetailsPage; 